/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import classNames from 'classnames';
import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import type { Transform } from '@dnd-kit/utilities';
import styles from './Item.module.css';
import { Button } from '@bynder/design-system';
import styled from 'styled-components';
import { IconDragIndicator } from '@bynder/icons';
import { CardWidgetCard } from './CardWidgetCard';
import { CardWidgetCard as TCardWidgetCard } from '../../../types';
import { CSS_CLASSES, CSS_PROPERTIES } from '../../guides/Inspector/constants';
import { useGuideStore } from '../../../stores/guideStore';
import { useWidgetStyleProperty } from '../../guides/Inspector/helpers';

interface Props {
  dragOverlay?: boolean;
  color?: string;
  disabled?: boolean;
  dragging?: boolean;
  handle?: boolean;
  handleProps?: any;
  index?: number;
  fadeIn?: boolean;
  transform?: Transform | null;
  listeners?: DraggableSyntheticListeners;
  sorting?: boolean;
  style?: React.CSSProperties;
  transition?: string | null;
  wrapperStyle?: React.CSSProperties;
  value: React.ReactNode;
  card: TCardWidgetCard;
  widgetStyle: string;
  isWidgetActive?: boolean;
  onRemove?(): void;
  renderItem?(args: {
    dragOverlay: boolean;
    dragging: boolean;
    sorting: boolean;
    index: number | undefined;
    fadeIn: boolean;
    listeners: DraggableSyntheticListeners;
    ref: React.Ref<HTMLElement>;
    style: React.CSSProperties | undefined;
    transform: Props['transform'];
    transition: Props['transition'];
    value: Props['value'];
  }): React.ReactElement;
}

export const Item = React.memo(
  React.forwardRef<HTMLLIElement, Props>(
    (
      {
        widgetStyle,
        card,
        color,
        dragOverlay,
        dragging,
        disabled,
        fadeIn,
        handle,
        handleProps,
        isWidgetActive,
        index,
        listeners,
        renderItem,
        sorting,
        style,
        transition,
        transform,
        value,
        wrapperStyle,
        ...props
      },
      ref,
    ) => {
      const isEditMode = useGuideStore(state => state.isEditMode);

      const { value: cardTitleFontFamily } = useWidgetStyleProperty(
        widgetStyle,
        'widgets.card.title.fontFamily',
        CSS_PROPERTIES.FONT_FAMILY,
        CSS_CLASSES.CARD_TITLE,
      );

      const { value: cardTitleFontWeight } = useWidgetStyleProperty(
        widgetStyle,
        'widgets.card.title.fontStyle',
        CSS_PROPERTIES.FONT_WEIGHT,
        CSS_CLASSES.CARD_TITLE,
      );

      const { value: cardTitleFontSize } = useWidgetStyleProperty(
        widgetStyle,
        'widgets.card.title.fontSize',
        CSS_PROPERTIES.FONT_SIZE,
        CSS_CLASSES.CARD_TITLE,
      );

      const { value: cardDescriptionFontFamily } = useWidgetStyleProperty(
        widgetStyle,
        'widgets.card.description.fontFamily',
        CSS_PROPERTIES.FONT_FAMILY,
        CSS_CLASSES.CARD_DESCRIPTION,
      );

      const { value: cardDescriptionFontWeight } = useWidgetStyleProperty(
        widgetStyle,
        'widgets.card.description.fontStyle',
        CSS_PROPERTIES.FONT_WEIGHT,
        CSS_CLASSES.CARD_DESCRIPTION,
      );

      const { value: cardDescriptionFontSize } = useWidgetStyleProperty(
        widgetStyle,
        'widgets.card.description.fontSize',
        CSS_PROPERTIES.FONT_SIZE,
        CSS_CLASSES.CARD_DESCRIPTION,
      );

      const { value: cardBorderRadius } = useWidgetStyleProperty(
        widgetStyle,
        'widgets.card.borderRadius',
        CSS_PROPERTIES.BORDER_RADIUS,
      );

      const { value: cardTitleColor } = useWidgetStyleProperty(
        widgetStyle,
        'widgets.card.titleColor.hexCode',
        CSS_PROPERTIES.COLOR,
        CSS_CLASSES.CARD_TITLE,
      );

      const { value: cardDescriptionColor } = useWidgetStyleProperty(
        widgetStyle,
        'widgets.card.descriptionColor.hexCode',
        CSS_PROPERTIES.COLOR,
        CSS_CLASSES.CARD_DESCRIPTION,
      );

      const { value: borderColor } = useWidgetStyleProperty(
        widgetStyle,
        'widgets.card.borderColor.hexCode',
        CSS_PROPERTIES.BORDER_COLOR,
      );

      useEffect(() => {
        if (!dragOverlay) {
          return;
        }

        document.body.style.cursor = 'grabbing';

        return () => {
          document.body.style.cursor = '';
        };
      }, [dragOverlay]);

      return renderItem ? (
        renderItem({
          dragOverlay: Boolean(dragOverlay),
          dragging: Boolean(dragging),
          sorting: Boolean(sorting),
          index,
          fadeIn: Boolean(fadeIn),
          listeners,
          ref,
          style,
          transform,
          transition,
          value,
        })
      ) : (
        <li
          className={classNames(
            styles.Wrapper,
            fadeIn && styles.fadeIn,
            sorting && styles.sorting,
            dragOverlay && styles.dragOverlay,
          )}
          style={
            {
              ...wrapperStyle,
              transition: [transition, wrapperStyle?.transition]
                .filter(Boolean)
                .join(', '),
              '--translate-x': transform
                ? `${Math.round(transform.x)}px`
                : undefined,
              '--translate-y': transform
                ? `${Math.round(transform.y)}px`
                : undefined,
              '--scale-x': transform?.scaleX
                ? `${transform.scaleX}`
                : undefined,
              '--scale-y': transform?.scaleY
                ? `${transform.scaleY}`
                : undefined,
              '--index': index,
              '--color': color,
            } as React.CSSProperties
          }
          ref={ref}
        >
          <div
            className={classNames(
              styles.Item,
              dragging && styles.dragging,
              handle && styles.withHandle,
              dragOverlay && styles.dragOverlay,
              disabled && styles.disabled,
              color && styles.color,
            )}
            style={style}
            data-cypress="draggable-item"
            {...(!handle ? listeners : undefined)}
            {...props}
            tabIndex={!handle ? 0 : undefined}
          >
            {card && (
              <CardWidgetCard
                index={index}
                isWidgetActive={isWidgetActive}
                isEditMode={isEditMode}
                cardTitle={card.headline.title}
                cardDescription={card.headline.description}
                titleColor={cardTitleColor}
                descriptionColor={cardDescriptionColor}
                borderColor={borderColor}
                cardImageUrl={card.asset.imageUrl}
                cardLink={card.link.url}
                cardId={card.id}
                openNewTab={card.link.openNewTab}
                $titleTypography={{
                  fontFamily: cardTitleFontFamily,
                  fontSize: Number(cardTitleFontSize),
                  fontStyle: cardTitleFontWeight,
                }}
                $descriptionTypography={{
                  fontFamily: cardDescriptionFontFamily,
                  fontSize: Number(cardDescriptionFontSize),
                  fontStyle: cardDescriptionFontWeight,
                }}
                $borderRadius={cardBorderRadius}
              />
            )}

            {handle && isWidgetActive && (
              <StyledButton
                isSmall
                {...listeners}
                {...handleProps}
                icon={<IconDragIndicator />}
              />
            )}
          </div>
        </li>
      );
    },
  ),
);

const StyledButton = styled(Button)`
  visibility: hidden;
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: grab;
`;
